/**
 * Панель массового ввода через Excel-шаблон.
 *
 * Форма ниже удобна для точечной правки, но заполнять территорию сразу за
 * несколько лет по одному полю — мучение, а собирать данные часто нужно офлайн.
 * Поэтому здесь вторая дорожка к тем же таблицам паспорта: скачать шаблон, уже
 * заполненный текущими значениями, дописать недостающее и загрузить обратно.
 *
 * Скачивается ШАБЛОН ВЫБРАННОЙ ТЕРРИТОРИИ — один лист на тот КАТО, что открыт в
 * кабинете, с колонками годов вокруг отчётного. Уровень территории роли не
 * играет, поэтому панели он больше не нужен.
 *
 * Загрузка двухшаговая: сначала предпросмотр (что распознано, что заменится,
 * что отклонено) и только потом применение. Замена блоков необратима, поэтому
 * оператор должен увидеть последствия до подтверждения.
 */
import { AlertTriangle, Download, FileUp, Loader2 } from 'lucide-react';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '../../../../components/ui/button';
import { Card, CardContent, CardHeader } from '../../../../components/ui/card';
import {
  applyTemplate,
  downloadTemplate,
  previewTemplate,
  TemplateFormatError,
  type TemplatePreview,
} from '../../../../requests/input_data.request';
import type { Lang } from '../formState';

export interface TemplatePanelProps {
  kato: number;
  year: number;
  lang: Lang;
  /**
   * Перечитать данные и показать отчёт после применения шаблона. Второй
   * аргумент — сколько ячеек отклонено, потому что показатель на источнике
   * (US-39 FR-5c.4): остальное применено.
   */
  onApplied: (message: string, rejectedBySource: number) => void;
}

type Stage =
  | { kind: 'idle' }
  | { kind: 'busy' }
  | { kind: 'preview'; file: File; preview: TemplatePreview }
  | { kind: 'error'; message: string };

export const TemplatePanel = ({
  kato,
  year,
  lang,
  onApplied,
}: TemplatePanelProps) => {
  const { t } = useTranslation();
  const fileRef = useRef<HTMLInputElement>(null);
  const [stage, setStage] = useState<Stage>({ kind: 'idle' });

  const messageOf = (ru: string, kz: string) => (lang === 'kz' && kz !== '' ? kz : ru);

  const handleDownload = async () => {
    setStage({ kind: 'busy' });
    try {
      const { blob, filename } = await downloadTemplate(kato, year);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.click();
      URL.revokeObjectURL(url);
      setStage({ kind: 'idle' });
    } catch {
      setStage({ kind: 'error', message: t('adminInput.templateDownloadFailed') });
    }
  };

  const handlePick = async (file: File | undefined) => {
    if (file === undefined) return;
    setStage({ kind: 'busy' });
    try {
      const preview = await previewTemplate(file);
      setStage({ kind: 'preview', file, preview });
    } catch (error) {
      setStage({
        kind: 'error',
        message:
          error instanceof TemplateFormatError
            ? messageOf(error.messageRu, error.messageKz)
            : t('adminInput.templateUploadFailed'),
      });
    } finally {
      if (fileRef.current !== null) fileRef.current.value = '';
    }
  };

  const handleApply = async () => {
    if (stage.kind !== 'preview') return;
    const { file } = stage;
    setStage({ kind: 'busy' });
    try {
      const result = await applyTemplate(file);
      setStage({ kind: 'idle' });
      onApplied(
        messageOf(result.message_ru, result.message_kz),
        result.rejected_by_source?.length ?? 0,
      );
    } catch (error) {
      setStage({
        kind: 'error',
        message:
          error instanceof TemplateFormatError
            ? messageOf(error.messageRu, error.messageKz)
            : t('adminInput.templateUploadFailed'),
      });
    }
  };

  const busy = stage.kind === 'busy';

  return (
    <Card className="shadow-card" data-testid="template-panel">
      <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-3 pb-3">
        <div className="min-w-0">
          <h3 className="text-base font-semibold">{t('adminInput.templateTitle')}</h3>
          <p className="mt-0.5 text-xs text-text-muted">
            {t('adminInput.templateSubtitle')}
          </p>
        </div>
        <div className="flex shrink-0 gap-2">
          <Button
            type="button"
            variant="ghost"
            disabled={busy}
            data-testid="template-download"
            onClick={() => void handleDownload()}
          >
            {busy ? (
              <Loader2 className="mr-2 size-4 animate-spin" />
            ) : (
              <Download className="mr-2 size-4" />
            )}
            {t('adminInput.templateDownload')}
          </Button>
          <Button
            type="button"
            variant="ghost"
            disabled={busy}
            data-testid="template-upload"
            onClick={() => fileRef.current?.click()}
          >
            <FileUp className="mr-2 size-4" />
            {t('adminInput.templateUpload')}
          </Button>
          <input
            ref={fileRef}
            type="file"
            accept=".xlsx"
            hidden
            data-testid="template-file"
            onChange={(event) => void handlePick(event.target.files?.[0])}
          />
        </div>
      </CardHeader>

      {stage.kind === 'error' && (
        <CardContent className="pt-0">
          <p
            className="flex items-start gap-2 rounded-md bg-status-danger/10 px-3 py-2 text-sm text-status-danger"
            data-testid="template-error"
          >
            <AlertTriangle className="mt-0.5 size-4 shrink-0" />
            {stage.message}
          </p>
        </CardContent>
      )}

      {stage.kind === 'preview' && (
        <CardContent className="space-y-3 pt-0" data-testid="template-preview">
          <p className="text-sm">
            {messageOf(stage.preview.message_ru, stage.preview.message_kz)}
          </p>

          {stage.preview.years.length > 0 && (
            <p className="text-xs text-text-muted" data-testid="template-years">
              {t('adminInput.templateYears', {
                years: stage.preview.years.join(', '),
              })}
            </p>
          )}

          {stage.preview.region_mismatch && (
            <p className="rounded-md bg-status-warning/10 px-3 py-2 text-sm">
              {t('adminInput.templateRegionMismatch')}
            </p>
          )}

          {stage.preview.overwrite_warnings.length > 0 && (
            <p className="rounded-md bg-status-warning/10 px-3 py-2 text-sm">
              {t('adminInput.templateOverwrite', {
                count: stage.preview.overwrite_warnings.length,
              })}{' '}
              {t('adminInput.templateOverwriteYears', {
                years: [
                  ...new Set(stage.preview.overwrite_warnings.map((w) => w.year)),
                ]
                  .sort((a, b) => a - b)
                  .join(', '),
              })}
            </p>
          )}

          {stage.preview.foreign_katos.length > 0 && (
            <p className="rounded-md bg-surface-muted px-3 py-2 text-sm text-text-muted">
              {t('adminInput.templateForeign', {
                count: stage.preview.foreign_katos.length,
              })}
            </p>
          )}

          {stage.preview.rejected_cells.length > 0 && (
            <details className="rounded-md bg-surface-muted px-3 py-2 text-sm">
              <summary className="cursor-pointer text-text-muted">
                {t('adminInput.templateRejected', {
                  count: stage.preview.rejected_cells.length,
                })}
              </summary>
              <ul className="mt-2 list-inside list-disc text-xs text-text-muted">
                {stage.preview.rejected_cells.slice(0, 20).map((cell, index) => (
                  <li key={`${cell.sheet}-${cell.row}-${cell.key}-${String(index)}`}>
                    {cell.sheet} · {cell.row} · {cell.key} — {cell.reason}
                  </li>
                ))}
              </ul>
            </details>
          )}

          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="ghost"
              onClick={() => setStage({ kind: 'idle' })}
            >
              {t('common.cancel')}
            </Button>
            <Button
              type="button"
              data-testid="template-apply"
              onClick={() => void handleApply()}
            >
              {t('adminInput.templateApply')}
            </Button>
          </div>
        </CardContent>
      )}
    </Card>
  );
};
